/**
 * A small expression language, parsed to an AST.
 *
 * The reason this exists rather than a JS arrow function plus a `formulaText`
 * string: those two silently diverge, and "the displayed formula matches the
 * displayed number" is the entire product. One string, one AST, four printers.
 *
 * Grammar:
 *   expr    := term (('+' | '-') term)*
 *   term    := power (('*' | '/') power)*
 *   power   := unary ('^' power)?           -- right associative
 *   unary   := '-' unary | primary
 *   primary := NUMBER | IDENT | IDENT '(' args ')' | '(' expr ')'
 */

import type { UnitId } from './schema.js';
import { type Dimension, dimDiv, dimEqual, dimMul, dimToString } from './units.js';

export type FnName = 'min' | 'max' | 'sum' | 'clamp' | 'pow' | 'abs';

export type Node =
  | { t: 'num'; v: number }
  | { t: 'ref'; id: string }
  | { t: 'op'; op: '+' | '-' | '*' | '/' | '^'; l: Node; r: Node }
  | { t: 'neg'; x: Node }
  | { t: 'call'; fn: FnName; args: Node[] };

const FUNCTIONS: Record<FnName, { arity: number | 'variadic' }> = {
  min: { arity: 'variadic' },
  max: { arity: 'variadic' },
  sum: { arity: 'variadic' },
  clamp: { arity: 3 },
  pow: { arity: 2 },
  abs: { arity: 1 },
};

// ---------------------------------------------------------------- lexer

type Tok =
  | { k: 'num'; v: number; pos: number }
  | { k: 'ident'; v: string; pos: number }
  | { k: 'punct'; v: string; pos: number }
  | { k: 'eof'; pos: number };

function lex(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const start = i;
      while (i < src.length && /[0-9._]/.test(src[i]!)) i++;
      // Scientific notation: 1e6, 1.5e-3
      if (src[i] === 'e' || src[i] === 'E') {
        const save = i;
        i++;
        if (src[i] === '+' || src[i] === '-') i++;
        if (/[0-9]/.test(src[i] ?? '')) {
          while (i < src.length && /[0-9]/.test(src[i]!)) i++;
        } else {
          i = save;
        }
      }
      const raw = src.slice(start, i).replace(/_/g, '');
      const v = Number(raw);
      if (!Number.isFinite(v)) throw new ExprError(`Bad number "${raw}"`, start, src);
      toks.push({ k: 'num', v, pos: start });
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const start = i;
      while (i < src.length && /[A-Za-z0-9_]/.test(src[i]!)) i++;
      toks.push({ k: 'ident', v: src.slice(start, i), pos: start });
      continue;
    }
    if ('+-*/^(),'.includes(c)) {
      toks.push({ k: 'punct', v: c, pos: i });
      i++;
      continue;
    }
    throw new ExprError(`Unexpected character "${c}"`, i, src);
  }
  toks.push({ k: 'eof', pos: src.length });
  return toks;
}

export class ExprError extends Error {
  constructor(msg: string, readonly pos: number, readonly src: string) {
    super(`${msg}\n  in: ${src}\n      ${' '.repeat(Math.max(0, pos))}^`);
    this.name = 'ExprError';
  }
}

// ---------------------------------------------------------------- parser

export function parse(src: string): Node {
  const toks = lex(src);
  let p = 0;

  const peek = (): Tok => toks[p]!;
  const next = (): Tok => toks[p++]!;

  function eatPunct(v: string): void {
    const t = peek();
    if (t.k !== 'punct' || t.v !== v) {
      throw new ExprError(`Expected "${v}"`, t.pos, src);
    }
    p++;
  }

  function isPunct(v: string): boolean {
    const t = peek();
    return t.k === 'punct' && t.v === v;
  }

  function parseExpr(): Node {
    let l = parseTerm();
    while (isPunct('+') || isPunct('-')) {
      const op = (next() as { v: string }).v as '+' | '-';
      l = { t: 'op', op, l, r: parseTerm() };
    }
    return l;
  }

  function parseTerm(): Node {
    let l = parsePower();
    while (isPunct('*') || isPunct('/')) {
      const op = (next() as { v: string }).v as '*' | '/';
      l = { t: 'op', op, l, r: parsePower() };
    }
    return l;
  }

  function parsePower(): Node {
    const base = parseUnary();
    if (isPunct('^')) {
      next();
      // Right associative: 2^3^2 === 2^(3^2)
      return { t: 'op', op: '^', l: base, r: parsePower() };
    }
    return base;
  }

  function parseUnary(): Node {
    if (isPunct('-')) {
      next();
      return { t: 'neg', x: parseUnary() };
    }
    if (isPunct('+')) {
      next();
      return parseUnary();
    }
    return parsePrimary();
  }

  function parsePrimary(): Node {
    const t = peek();
    if (t.k === 'num') {
      next();
      return { t: 'num', v: t.v };
    }
    if (t.k === 'ident') {
      next();
      if (isPunct('(')) {
        const fn = t.v as FnName;
        if (!(fn in FUNCTIONS)) throw new ExprError(`Unknown function "${t.v}"`, t.pos, src);
        next();
        const args: Node[] = [];
        if (!isPunct(')')) {
          args.push(parseExpr());
          while (isPunct(',')) {
            next();
            args.push(parseExpr());
          }
        }
        eatPunct(')');
        const spec = FUNCTIONS[fn];
        if (spec.arity !== 'variadic' && args.length !== spec.arity) {
          throw new ExprError(
            `${fn}() takes ${spec.arity} arguments, got ${args.length}`,
            t.pos,
            src,
          );
        }
        if (spec.arity === 'variadic' && args.length === 0) {
          throw new ExprError(`${fn}() needs at least one argument`, t.pos, src);
        }
        return { t: 'call', fn, args };
      }
      return { t: 'ref', id: t.v };
    }
    if (t.k === 'punct' && t.v === '(') {
      next();
      const inner = parseExpr();
      eatPunct(')');
      return inner;
    }
    throw new ExprError('Expected a number, name, or "("', t.pos, src);
  }

  const node = parseExpr();
  const end = peek();
  if (end.k !== 'eof') throw new ExprError('Unexpected trailing input', end.pos, src);
  return node;
}

// ---------------------------------------------------------------- refs

/** Every input id this expression depends on, in first-appearance order. */
export function refs(n: Node): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  walk(n, (node) => {
    if (node.t === 'ref' && !seen.has(node.id)) {
      seen.add(node.id);
      out.push(node.id);
    }
  });
  return out;
}

export function walk(n: Node, fn: (n: Node) => void): void {
  fn(n);
  switch (n.t) {
    case 'op':
      walk(n.l, fn);
      walk(n.r, fn);
      break;
    case 'neg':
      walk(n.x, fn);
      break;
    case 'call':
      for (const a of n.args) walk(a, fn);
      break;
    default:
      break;
  }
}

// ---------------------------------------------------------------- evaluate

export type NumScope = Record<string, number>;

export function evalNode(n: Node, scope: NumScope): number {
  switch (n.t) {
    case 'num':
      return n.v;
    case 'ref': {
      const v = scope[n.id];
      if (v === undefined) throw new Error(`Unknown input "${n.id}"`);
      return v;
    }
    case 'neg':
      return -evalNode(n.x, scope);
    case 'op': {
      const a = evalNode(n.l, scope);
      const b = evalNode(n.r, scope);
      switch (n.op) {
        case '+':
          return a + b;
        case '-':
          return a - b;
        case '*':
          return a * b;
        case '/':
          return b === 0 ? 0 : a / b;
        case '^':
          return Math.pow(a, b);
      }
      break;
    }
    case 'call': {
      const a = n.args.map((x) => evalNode(x, scope));
      switch (n.fn) {
        case 'min':
          return Math.min(...a);
        case 'max':
          return Math.max(...a);
        case 'sum':
          return a.reduce((s, x) => s + x, 0);
        case 'clamp':
          return Math.min(Math.max(a[0]!, a[1]!), a[2]!);
        case 'pow':
          return Math.pow(a[0]!, a[1]!);
        case 'abs':
          return Math.abs(a[0]!);
      }
    }
  }
  throw new Error('Unreachable');
}

// ---------------------------------------------------------------- dimensions

export interface DimScope {
  [id: string]: Dimension;
}

/**
 * Infer the dimension of an expression, throwing on a mismatch.
 *
 * Catching `usd + job_years` at validate time is worth the hour this took.
 */
export function inferDim(n: Node, scope: DimScope, src = ''): Dimension {
  switch (n.t) {
    case 'num':
      return {};
    case 'ref': {
      const d = scope[n.id];
      if (!d) throw new Error(`Unknown input "${n.id}" in dimension check`);
      return { ...d };
    }
    case 'neg':
      return inferDim(n.x, scope, src);
    case 'op': {
      const a = inferDim(n.l, scope, src);
      const b = inferDim(n.r, scope, src);
      switch (n.op) {
        case '+':
        case '-': {
          // A bare number may be added to anything (e.g. `1 - abatement_share`).
          if (n.l.t === 'num' && Object.keys(a).length === 0) return b;
          if (n.r.t === 'num' && Object.keys(b).length === 0) return a;
          if (!dimEqual(a, b)) {
            throw new Error(
              `Cannot ${n.op === '+' ? 'add' : 'subtract'} ${dimToString(a)} and ${dimToString(b)}` +
                (src ? `\n  in: ${src}` : ''),
            );
          }
          return a;
        }
        case '*':
          return dimMul(a, b);
        case '/':
          return dimDiv(a, b);
        case '^':
          return a; // exponent must be dimensionless; enforced below
      }
      break;
    }
    case 'call': {
      const ds = n.args.map((x) => inferDim(x, scope, src));
      switch (n.fn) {
        case 'abs':
          return ds[0]!;
        case 'pow':
          return ds[0]!;
        case 'min':
        case 'max':
        case 'sum':
        case 'clamp': {
          const first = ds[0]!;
          for (const d of ds.slice(1)) {
            if (!dimEqual(first, d)) {
              throw new Error(
                `${n.fn}() arguments have mismatched units: ${dimToString(first)} vs ${dimToString(d)}` +
                  (src ? `\n  in: ${src}` : ''),
              );
            }
          }
          return first;
        }
      }
    }
  }
  throw new Error('Unreachable');
}

// ---------------------------------------------------------------- printers

const PREC: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2, '^': 3 };

export interface PrintOpts {
  /** id → display label. Falls back to the raw id. */
  labels?: Record<string, string>;
  /** id → formatted value string, for the substituted printer. */
  values?: Record<string, string>;
  /** '×' and '÷' read better than '*' and '/' for a general audience. */
  prettyOps?: boolean;
}

function opSymbol(op: string, pretty: boolean): string {
  if (!pretty) return op;
  if (op === '*') return '×';
  if (op === '/') return '÷';
  return op;
}

function needsParens(child: Node, parentOp: string, side: 'l' | 'r'): boolean {
  if (child.t !== 'op') return false;
  const cp = PREC[child.op]!;
  const pp = PREC[parentOp]!;
  if (cp < pp) return true;
  if (cp > pp) return false;
  // Same precedence: right operand of - and / needs parens.
  if (side === 'r' && (parentOp === '-' || parentOp === '/')) return true;
  return false;
}

function printWith(n: Node, opts: PrintOpts, leaf: (id: string) => string): string {
  const pretty = opts.prettyOps ?? true;
  const go = (node: Node): string => {
    switch (node.t) {
      case 'num':
        return formatLiteral(node.v, pretty);
      case 'ref':
        return leaf(node.id);
      case 'neg':
        return `-${go(node.x)}`;
      case 'op': {
        const l = needsParens(node.l, node.op, 'l') ? `(${go(node.l)})` : go(node.l);
        const r = needsParens(node.r, node.op, 'r') ? `(${go(node.r)})` : go(node.r);
        const sym = opSymbol(node.op, pretty);
        return node.op === '^' ? `${l}^${r}` : `${l} ${sym} ${r}`;
      }
      case 'call':
        return `${node.fn}(${node.args.map(go).join(', ')})`;
    }
  };
  return go(n);
}

/**
 * `prettyOps: false` means machine-readable — the output must survive being
 * fed back through parse(). Thousands separators are display-only, so they are
 * tied to the same flag as × and ÷.
 */
function formatLiteral(v: number, pretty: boolean): string {
  if (pretty && Number.isInteger(v) && Math.abs(v) >= 1000) {
    return new Intl.NumberFormat('en-US').format(v);
  }
  return String(v);
}

/** `school_levy_share × taxable_value − state_aid_offset` */
export function printSymbolic(n: Node, opts: PrintOpts = {}): string {
  return printWith(n, opts, (id) => opts.labels?.[id] ?? id);
}

/** `0.31 × ($2.1B–$3.4B) − $1.9M` */
export function printSubstituted(n: Node, opts: PrintOpts = {}): string {
  return printWith(n, opts, (id) => opts.values?.[id] ?? id);
}

export interface Token {
  kind: 'ref' | 'num' | 'op' | 'paren' | 'fn' | 'sep';
  text: string;
  /** Present when kind === 'ref'. Drives the clickable provenance chip. */
  id?: string;
}

/**
 * Structured tokens so each identifier can render as a clickable chip that
 * opens that input's own provenance drawer. This is what makes provenance
 * transitively navigable rather than a flat list.
 */
export function printTokens(n: Node, opts: PrintOpts = {}): Token[] {
  const pretty = opts.prettyOps ?? true;
  const out: Token[] = [];
  const emit = (kind: Token['kind'], text: string, id?: string) => {
    const tok: Token = { kind, text };
    if (id !== undefined) tok.id = id;
    out.push(tok);
  };

  const go = (node: Node): void => {
    switch (node.t) {
      case 'num':
        emit('num', formatLiteral(node.v, pretty));
        return;
      case 'ref':
        emit('ref', opts.labels?.[node.id] ?? node.id, node.id);
        return;
      case 'neg':
        emit('op', '-');
        go(node.x);
        return;
      case 'op': {
        const lp = needsParens(node.l, node.op, 'l');
        const rp = needsParens(node.r, node.op, 'r');
        if (lp) emit('paren', '(');
        go(node.l);
        if (lp) emit('paren', ')');
        emit('op', opSymbol(node.op, pretty));
        if (rp) emit('paren', '(');
        go(node.r);
        if (rp) emit('paren', ')');
        return;
      }
      case 'call': {
        emit('fn', node.fn);
        emit('paren', '(');
        node.args.forEach((a, i) => {
          if (i > 0) emit('sep', ',');
          go(a);
        });
        emit('paren', ')');
        return;
      }
    }
  };
  go(n);
  return out;
}

/**
 * Intermediate results for multi-term expressions, so a reader can walk the
 * arithmetic rather than trusting one collapsed answer.
 *
 * Only the top-level additive/subtractive terms are broken out; going deeper
 * produces noise rather than clarity.
 */
export function topLevelTerms(n: Node): { sign: 1 | -1; node: Node }[] {
  const terms: { sign: 1 | -1; node: Node }[] = [];
  const go = (node: Node, sign: 1 | -1): void => {
    if (node.t === 'op' && (node.op === '+' || node.op === '-')) {
      go(node.l, sign);
      go(node.r, node.op === '-' ? ((-sign) as 1 | -1) : sign);
      return;
    }
    terms.push({ sign, node });
  };
  go(n, 1);
  return terms;
}

export function exprUnitHint(unit: UnitId): string {
  return unit;
}
